import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import CTABand from "@/components/CTABand";
import { Container, Reveal, Counter, ImageReveal } from "@/components/Reveal";
import { VALUES, STATS, IMG, COMPANY } from "@/data/content";

export default function About() {
  return (
    <>
      <SEO
        title="About"
        description="Innervation Energy Group is a commercial energy advisory firm built on independence, transparency, and deep market expertise."
        path="/about"
      />
      <PageHero
        overline="About"
        titleLines={["A trusted advisor", "for commercial energy."]}
        subtitle="We exist to help businesses make confident, informed energy decisions — with advice that always serves your interests."
        crumbs={[{ label: "About", path: "/about" }]}
        image={IMG.boardroom}
      />

      {/* Story */}
      <section className="bg-white py-24 md:py-32">
        <Container>
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-5">
              <p className="overline text-electric">Our Story</p>
              <h2 className="mt-4 font-heading text-3xl font-semibold leading-[1.12] tracking-tight text-navy md:text-4xl">
                Built to bring clarity to a complex market.
              </h2>
            </div>
            <div className="space-y-6 text-lg leading-relaxed text-slatey lg:col-span-7">
              <p>
                Commercial energy markets are complex, fast-moving, and full of
                incentives that don't always align with the buyer. Innervation Energy
                Group was founded to change that — to give businesses an independent
                advisor with the expertise to navigate procurement strategically.
              </p>
              <p>
                We focus exclusively on commercial and industrial organizations. From
                a single facility to a national multi-site portfolio, we bring
                wholesale-level market intelligence, disciplined process, and honest
                education to every engagement.
              </p>
              <p>
                Our approach is simple: understand your business, model your options,
                and recommend the strategy that serves you best — then stay engaged as
                markets and needs evolve.
              </p>
            </div>
          </div>
        </Container>
      </section>

      {/* Mission / Vision */}
      <section className="bg-ice py-24 md:py-28">
        <Container>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <Reveal>
              <div className="h-full border border-line bg-white p-10">
                <p className="overline text-electric">Mission</p>
                <p className="mt-5 font-heading text-2xl leading-snug text-navy md:text-3xl">
                  To help businesses make smarter energy decisions through expertise,
                  transparency, and strategic guidance.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="h-full border border-line bg-white p-10">
                <p className="overline text-electric">Vision</p>
                <p className="mt-5 font-heading text-2xl leading-snug text-navy md:text-3xl">
                  To be the most trusted commercial energy advisor in every
                  deregulated market we serve.
                </p>
              </div>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* Values */}
      <section className="bg-white py-24 md:py-28">
        <Container>
          <p className="overline text-electric">Our Values</p>
          <h2 className="mt-4 font-heading text-3xl font-semibold tracking-tight text-navy md:text-4xl">
            What guides our advice.
          </h2>
          <div className="mt-12 grid grid-cols-1 border-l border-t border-line sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, i) => (
              <Reveal key={i} delay={i * 0.07} className="border-b border-r border-line">
                <div className="p-8">
                  <span className="font-mono text-sm text-slatey">0{i + 1}</span>
                  <h3 className="mt-4 font-heading text-xl font-medium text-navy">
                    {v.title}
                  </h3>
                  <p className="mt-3 leading-relaxed text-slatey">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* Stats + logo */}
      <section className="relative overflow-hidden bg-navy py-24 text-white md:py-28">
        <div className="grid-texture pointer-events-none absolute inset-0 opacity-40" />
        <Container className="relative">
          <div className="grid grid-cols-2 gap-8 lg:grid-cols-4">
            {STATS.map((s, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div>
                  <div className="font-heading text-4xl font-semibold tracking-tight md:text-5xl">
                    <Counter value={s.value} suffix={s.suffix} />
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-white/60">{s.label}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-20 flex flex-col items-center border-t border-white/10 pt-16 text-center">
            <img
              src="/ieg-logo-on-dark.png"
              alt={COMPANY.name}
              className="h-16 w-auto md:h-20"
            />
            <p className="mt-8 max-w-xl text-lg text-white/60">
              {COMPANY.tagline} — {COMPANY.positioning}
            </p>
          </div>
        </Container>
      </section>

      <CTABand />
    </>
  );
}
